import { Box, Link, Stack, SxProps } from '@mui/material';
import { Button } from 'system/atoms';

const editLinkSx: SxProps = {
  cursor: 'pointer'
};

const buttonContainerSx: SxProps = {
  display: 'flex',
  gap: 2,
  mt: 2
};
import { SummaryField } from 'common/ui/SummaryField';
import { useInitializeCandidateBatchMutation } from 'generated-graphql/hooks/apollo-hooks';
import { AutomationTaskInput } from 'generated-graphql/types';
import { useGetCampaignNames } from 'modules/analyse/hooks/useGetCampaignNames';
import { FileWithS3Key } from 'pages/Analyse/NoATSAnalyseCreation';
import { AnalysisRequirementInput } from 'modules/analyse/types/AnalysisRequirementInput';
import { mapToSummaryAnalyse } from 'modules/analyse/utils/mapToSummaryAnalyse';
import { useNavigate } from 'react-router-dom';
import { initializeCandidateBatchGroup } from 'services/candidate/api';
import { useLoadingOverlay } from 'services/loading/hooks';
import { Paths } from 'services/navigation/paths';
import { ToastType, useToast } from 'services/toast';

type Props = {
  candidateBatchGroupName: string | undefined;
  filesWithS3Keys: FileWithS3Key[];
  jobDescription: string;
  externalId?: string;
  requirements: AnalysisRequirementInput[];
  automationTasks: AutomationTaskInput[];
  onBack: () => void;
};

export const NoATSRequirementsSummaryStep: React.FC<Props> = ({
  candidateBatchGroupName,
  filesWithS3Keys,
  jobDescription,
  externalId,
  requirements,
  automationTasks,
  onBack
}) => {
  const { showLoadingOverlay, hideLoadingOverlay } = useLoadingOverlay();
  const { showToast } = useToast();
  const navigate = useNavigate();

  const [initializeCandidateBatchMutation] =
    useInitializeCandidateBatchMutation();

  const { campaignNames } = useGetCampaignNames({
    automationTasks: automationTasks || []
  });

  const summaryAnalyse = mapToSummaryAnalyse({
    campaignNames,
    rulesetChosenTitle: candidateBatchGroupName,
    requirements,
    automationTasks: automationTasks || [],
    externalId
  });

  const handleSubmit = async () => {
    showLoadingOverlay();

    if (candidateBatchGroupName) {
      await initializeCandidateBatchGroup({
        name: candidateBatchGroupName,
        automationTasks,
        analysisRequirements: requirements
      });
    }

    const result = await initializeCandidateBatchMutation({
      variables: {
        input: {
          cvS3Keys: filesWithS3Keys.map((fileWithS3Key) => fileWithS3Key.s3Key),
          jobDescription,
          externalId,
          requirements,
          automationTasks
        }
      }
    });

    const id = result?.data?.initializeCandidateBatch?.id;

    if (!id) {
      showToast({
        message: 'Something went wrong',
        type: ToastType.Error
      });
      hideLoadingOverlay();
      return;
    }

    showToast({
      message: 'Analysis created successfully',
      type: ToastType.Success
    });
    hideLoadingOverlay();
    navigate(`${Paths.ANALYSE}/${id}`);
  };

  const orderedFields = [
    'externalId',
    'ruleset',
    'mandatoryRequirements',
    'optionalRequirements',
    'rules',
    'actions'
  ] as const;

  const renderSummaryItem = (
    fieldKey: string,
    field: { label: string; value: string[] | string },
    showEditLink = true
  ) => (
    <Stack
      key={fieldKey}
      direction="column"
      gap={fieldKey === 'mandatoryRequirements' ? '10px' : '20px'}
      alignItems="start"
    >
      <SummaryField label={field.label} value={field.value} />
      {showEditLink && (
        <Link
          onClick={onBack}
          underline="none"
          fontWeight="bold"
          sx={editLinkSx}
        >
          Edit
        </Link>
      )}
    </Stack>
  );

  return (
    <Stack direction="column" gap="40px" alignItems="start">
      {orderedFields.map((fieldKey) => {
        const field = summaryAnalyse[fieldKey as keyof typeof summaryAnalyse];
        if (!field) return null;

        // Special case for mandatoryRequirements - only show edit link on optionalRequirements
        if (fieldKey === 'mandatoryRequirements') {
          return renderSummaryItem(fieldKey, field, false);
        }

        return renderSummaryItem(fieldKey, field, true);
      })}

      <Box sx={buttonContainerSx}>
        <Button variant="primary" onClick={handleSubmit}>
          Launch analysis
        </Button>
      </Box>
    </Stack>
  );
};
